'use client';

import { useMutation } from '@tanstack/react-query';
import classNames from 'classnames';
import { type jsPDF } from 'jspdf';
import React, { type ForwardedRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { postEmailWithPressReleasePdf } from '@module/mdrt-org/dashboard/dashboard-page-content/components/pdf-modal-wrapper/press-release-pdf-modal/utils/post-email-with-press-release-pdf';
import { FieldController } from '@module/mdrt-org/shared/components/field-controller/field-controller';
import { MAX_EMAIL_LENGTH } from '@module/mdrt-org/shared/utils/constants/input-constants';
import { isMobileUserAgent } from '@module/mdrt-org/shared/utils/helpers/is-mobile-device';
import { type FetchError } from '@module/mdrt-org/shared/utils/http-services/error-definitions';
import { EMAIL_REGEXP } from '@module/mdrt-org/shared/utils/types/regexp';
import { Alert } from '@shared/ui-components/alert/alert';
import { AlertVariant } from '@shared/ui-components/alert/alertVariant';
import { CheckboxField } from '@shared/ui-components/checkbox-field/checkbox-field';
import { FormInput } from '@shared/ui-components/form-input/form-input';
import { FormWrapper } from '@shared/ui-components/form-wrapper/form-wrapper';
import { PdfIcon } from '@shared/ui-components/svgs';
import {
  GENERIC_ERROR_MESSAGE_FALLBACK,
  GENERIC_ERROR_TRANSLATION_KEY,
} from '@shared/utils/constants/errors';
import { formBaseConfig } from '@shared/utils/constants/formBaseConfig';

import styles from './pdf-download-modal-content.module.scss';
import { sendGtmEvent } from '@shared/utils/helpers/send-gtm-event';
import {
  GTM_ACTION_CLICK,
  GTM_EVENT_NAME_INTERNAL_LINK_CLICK,
  GTM_EVENT_USER_INTERACTIONS,
} from '@shared/utils/constants/gtm';

type PressReleasePdfDownloadForm = {
  email: string;
  shouldSendByEmail: boolean;
};

type PdfDownloadModalContentProps = {
  cmsContent: {
    description: string;
    emailFieldLabel: string;
    errorMessage: string;
    invalidEmailValidationMessage: string;
    previewButtonLabel: string;
    requiredLabel: string;
    sendByEmailCheckboxLabel: string;
  };
  emailContent: {
    emailAddress: string;
    emailBody: string;
    emailSubject: string;
    fileName: string;
    fullName: string;
    pdfDocument: jsPDF;
  };
  handleModalClose: () => void;
  isPreviewMode: boolean;
  setIsPDFGenerating: React.Dispatch<React.SetStateAction<boolean>>;
  setIsPreviewMode: React.Dispatch<React.SetStateAction<boolean>>;
  templateName: string;
};

export const PdfDownloadModalContent = React.forwardRef<
  HTMLIFrameElement,
  PdfDownloadModalContentProps
>(
  (
    {
      cmsContent,
      emailContent: { emailAddress, emailBody, emailSubject, fileName, fullName, pdfDocument },
      handleModalClose,
      isPreviewMode,
      setIsPDFGenerating,
      setIsPreviewMode,
      templateName,
    },
    ref: ForwardedRef<HTMLIFrameElement>
  ) => {
    const [downloadStepErrorMessage, setDownloadStepErrorMessage] = useState<string | undefined>();

    const formMethods = useForm<PressReleasePdfDownloadForm>({
      ...formBaseConfig,
      defaultValues: {
        email: emailAddress ?? '',
        shouldSendByEmail: false,
      },
    });

    const { resetField, watch } = formMethods;
    const watchSendByEmailCheckbox = watch('shouldSendByEmail');

    const { mutate } = useMutation({
      mutationFn: postEmailWithPressReleasePdf,
      mutationKey: [],
      onError: (error: FetchError) => {
        setIsPDFGenerating(false);
        setDownloadStepErrorMessage(
          error?.messageTranslationKey === GENERIC_ERROR_TRANSLATION_KEY && cmsContent.errorMessage
            ? cmsContent.errorMessage
            : error.message
        );
      },
      onSuccess: () => {
        setIsPDFGenerating(false);
        if (!isMobileUserAgent(navigator.userAgent)) {
          handleModalClose();
        }
      },
    });

    const handleSubmit = async (formData: PressReleasePdfDownloadForm) => {
      setIsPDFGenerating(true);
      sendGtmEvent({
        action: GTM_ACTION_CLICK,
        event: GTM_EVENT_USER_INTERACTIONS,
        eventName: GTM_EVENT_NAME_INTERNAL_LINK_CLICK,
        link_url: 'Download',
        link_label: templateName,
        label: 'MemberKit-PressReleaseTemplate',
      });

      try {
        const blob = pdfDocument.output('blob');

        pdfDocument.save(fileName);

        if (formData.shouldSendByEmail) {
          const arrayBuffer = await blob.arrayBuffer();
          const encodedPdf = btoa(
            new Uint8Array(arrayBuffer).reduce(
              (data, byte) => data + String.fromCodePoint(byte),
              ''
            )
          );

          mutate({
            emailBody,
            emailSubject,
            encodedPdf,
            fileName,
            recipientEmail: formData.email,
            recipientName: fullName,
          });
        }

        setIsPDFGenerating(false);
        if (!isMobileUserAgent(navigator.userAgent)) {
          handleModalClose();
        }
      } catch {
        setIsPDFGenerating(false);
        setDownloadStepErrorMessage(GENERIC_ERROR_MESSAGE_FALLBACK);
      }
    };

    return (
      <div
        className={classNames(styles['modal-content'], {
          [styles['modal-content--preview']]: isPreviewMode,
        })}
      >
        {downloadStepErrorMessage && (
          <Alert
            className={styles['visa-letter-pdf-modal-download__message']}
            message={downloadStepErrorMessage}
            variant={AlertVariant.Error}
          />
        )}

        {/* eslint-disable-next-line react/iframe-missing-sandbox */}
        <iframe className={styles['modal-content__iframe']} ref={ref} />

        <div className={styles['modal-content__download-container']}>
          <div className={styles['modal-content__description']}>{cmsContent.description}</div>
          {!isMobileUserAgent(navigator.userAgent) && (
            <button
              className={styles['modal-content__preview-button']}
              onClick={() => setIsPreviewMode(true)}
            >
              <PdfIcon className={styles['modal-content__preview-button-icon']} />
              {cmsContent.previewButtonLabel}
            </button>
          )}
          <FormWrapper
            className={styles['modal-content__form']}
            dataTestId="press-release-pdf-download-preview-form"
            formMethods={formMethods}
            id="press-release-pdf-download-form"
            onSubmit={handleSubmit}
          >
            <CheckboxField
              label={cmsContent.sendByEmailCheckboxLabel}
              name="shouldSendByEmail"
              onChange={() => resetField('email')}
            />
            {watchSendByEmailCheckbox === true && (
              <FieldController
                label={cmsContent.emailFieldLabel}
                name="email"
                rules={{
                  pattern: {
                    message: cmsContent.invalidEmailValidationMessage,
                    value: EMAIL_REGEXP,
                  },
                  required: cmsContent.requiredLabel,
                }}
              >
                <FormInput maxLength={MAX_EMAIL_LENGTH} type="email" />
              </FieldController>
            )}
          </FormWrapper>
        </div>
      </div>
    );
  }
);
